import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AssignmentTypeFormModal from '../components/assignmentSettings/AssignmentTypeFormModal';
import AssignmentTypeTable from '../components/assignmentSettings/AssignmentTypeTable';
import {
  createAssignmentType,
  initializeDefaultAssignmentTypes,
  subscribeAssignmentTypes,
  toggleAssignmentTypeStatus,
  updateAssignmentType,
} from '../services/assignmentTypeService';

export default function AssignmentSettings() {
  const { userData } = useAuth();
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [initializing, setInitializing] = useState(false);

  const role = String(userData?.role || '').toLowerCase();
  const isAdmin = role === 'admin';

  useEffect(() => subscribeAssignmentTypes(
    setAssignmentTypes,
    (err) => setError(`Could not load assignment types: ${err.message}`)
  ), []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return assignmentTypes.filter((item) => {
      if (statusFilter === 'active' && item.isActive === false) return false;
      if (statusFilter === 'inactive' && item.isActive !== false) return false;
      if (!term) return true;
      return String(item.name || '').toLowerCase().includes(term);
    });
  }, [assignmentTypes, search, statusFilter]);

  if (!isAdmin) {
    return (
      <div className="bg-white rounded shadow p-8">
        <h1 className="text-2xl font-bold mb-2">Assignment Settings</h1>
        <p className="text-red-700">Only Admin can manage Assignment Settings.</p>
      </div>
    );
  }

  const save = async (values) => {
    try {
      setError('');
      setMessage('');
      if (editing) await updateAssignmentType(editing.id, values);
      else await createAssignmentType(values, userData);
      setFormOpen(false);
      setEditing(null);
      setMessage(editing ? 'Assignment type updated.' : 'Assignment type added.');
    } catch (err) {
      setError(`Could not save assignment type: ${err.message}`);
    }
  };

  const toggleStatus = async (item) => {
    const nextStatus = item.isActive === false;
    const action = nextStatus ? 'activate' : 'deactivate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} “${item.name}”?`)) return;
    try {
      setError('');
      await toggleAssignmentTypeStatus(item.id, nextStatus);
    } catch (err) {
      setError(`Could not update status: ${err.message}`);
    }
  };

  const initializeDefaults = async () => {
    try {
      setInitializing(true);
      setError('');
      setMessage('');
      const count = await initializeDefaultAssignmentTypes(assignmentTypes, userData);
      setMessage(count ? `${count} default assignment types added.` : 'All default assignment types are already available.');
    } catch (err) {
      setError(`Could not initialize defaults: ${err.message}`);
    } finally {
      setInitializing(false);
    }
  };

  const counts = {
    total: assignmentTypes.length,
    active: assignmentTypes.filter((item) => item.isActive !== false).length,
    matter: assignmentTypes.filter((item) => item.isActive !== false && item.requiresMatter).length,
    inactive: assignmentTypes.filter((item) => item.isActive === false).length,
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Assignment Settings</h1>
          <p className="text-sm text-gray-500">Manage assignment types and identify which types require a Matter / Case Diary.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={initializeDefaults} disabled={initializing} className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-60">
            {initializing ? 'Initializing...' : 'Initialize Defaults'}
          </button>
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            + Add Assignment Type
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Total Types</div><div className="text-2xl font-bold">{counts.total}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Active</div><div className="text-2xl font-bold text-green-700">{counts.active}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Require Matter</div><div className="text-2xl font-bold text-purple-700">{counts.matter}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Inactive</div><div className="text-2xl font-bold text-red-700">{counts.inactive}</div></div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-800 border border-red-200 rounded">{error}</div>}
      {message && <div className="mb-4 p-3 bg-green-100 text-green-800 border border-green-200 rounded">{message}</div>}

      <div className="bg-white rounded shadow p-4 mb-4 flex flex-col md:flex-row gap-3">
        <input value={search} onChange={(event) => setSearch(event.target.value)} className="flex-1 px-3 py-2 border rounded" placeholder="Search assignment type" />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="px-3 py-2 border rounded md:w-44">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <AssignmentTypeTable assignmentTypes={filtered} onEdit={(item) => { setEditing(item); setFormOpen(true); }} onToggleStatus={toggleStatus} />

      <AssignmentTypeFormModal
        open={formOpen}
        assignmentType={editing}
        existingTypes={assignmentTypes}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSave={save}
      />
    </div>
  );
}
